import { toTypedSchema } from '@vee-validate/yup'
import * as yup from 'yup'
import { useForm } from 'vee-validate'
import { customFetch } from '~~/openapi-client/custom-fetcher'
import {
	getUpdateManageUsersToAdminUrl,
	type updateManageUsersToAdminResponse,
} from '~~/openapi-client/gen/api/association-membership-rest-controller/association-membership-rest-controller'
import type { FileWithName } from '~/utils/blob'

// NOTE: Weil die Form auf mehreren Seiten aufgeteilt ist, wird daher das Schema global definiert
const schema = toTypedSchema(yup.object({
	boardRoleRequest: yup.object({
		functionalTagName: yup
			.string()
			.oneOf(['Vorstand', 'Stellvertreter'])
			.required(),
		resign: yup
			.boolean()
			.required(),
	}).required(),
	electionProtocol: yup
		.mixed<FileWithName>()
		.required('Die Datei ist erforderlich.'),
	articlesOfAssociation: yup
		.mixed<FileWithName>()
		.required('Die Datei ist erforderlich.'),
	registerExcerpt: yup
		.mixed<FileWithName>()
		.required('Die Datei ist erforderlich.'),
}))

const {
	handleSubmit,
	errors,
	defineField,
	validate,
	validateField,
	resetForm,
	isSubmitting,
} = useForm({
	validationSchema: schema,
	initialValues: {
		boardRoleRequest: {
			functionalTagName: 'Vorstand',
			resign: false,
		},
	},
})

const [functionalTagName, functionalTagNameAttrs] = defineField('boardRoleRequest.functionalTagName')
const [resign, resignAttrs] = defineField('boardRoleRequest.resign')
const [electionProtocol, electionProtocolAttrs] = defineField('electionProtocol')
const [articlesOfAssociation, articlesOfAssociationAttrs] = defineField('articlesOfAssociation')
const [registerExcerpt, registerExcerptAttrs] = defineField('registerExcerpt')

export default function useTransferBoardMembershipForm() {
	/**
	 * Befördert eine bestehende Mitgliedschaft mit der Rolle `Mitgliederverwalter` zu einer Vorstandsrolle
	 * (Vorstand oder Stellvertreter) und sendet anschließend die zugehörigen Vorstandsdokumente per E-Mail.
	 * @param id Die UUID der zu befördernden Mitgliedschaft
	 */
	const submit = (id: string) => handleSubmit(async (formValues) => {
		const formData = new FormData()

		if (!formValues.boardRoleRequest) {
			throw new Error('Board role request is required')
		}

		// Add nested fields with dot notation
		formData.append('boardRoleRequest.functionalTagName', formValues.boardRoleRequest.functionalTagName)
		formData.append('boardRoleRequest.resign', formValues.boardRoleRequest.resign.toString())

		// Add files (extract blob from FileWithName)
		formData.append('registerExcerpt', formValues.registerExcerpt.blob)
		formData.append('electionProtocol', formValues.electionProtocol.blob)
		formData.append('articlesOfAssociation', formValues.articlesOfAssociation.blob)

		return await customFetch<updateManageUsersToAdminResponse>(
			getUpdateManageUsersToAdminUrl(id),
			{
				method: 'PUT',
				body: formData,
			},
		)
	})()

	return {
		errors,
		functionalTagName,
		functionalTagNameAttrs,
		resign,
		resignAttrs,
		electionProtocol,
		electionProtocolAttrs,
		articlesOfAssociation,
		articlesOfAssociationAttrs,
		registerExcerpt,
		registerExcerptAttrs,
		isSubmitting,
		submit,
		validate,
		validateField,
		resetForm,
	}
}
